// client/src/pages/EntityBankPromptGenerator.js
//
// Entity Bank Prompt Generator — optional workflow for generating and saving prompts.
//
// Users can:
//   1. Select entities from the confirmed ENQ surface (basket interface)
//   2. Generate AI prompts from those entities
//   3. Review and edit the prompts (grouped A–E)
//   4. Recommend URLs based on entities
//   5. Save the prompt set to entity_sets
//   6. Then optionally import into CampaignBuilder
//
// This is a SEPARATE workflow from CampaignBuilder — users can skip it entirely
// and build campaigns manually. This is for teams that want AI-generated prompts.
//
// Route: /project/:projectId/entity-bank-prompt-generator
// Lives at: client/src/pages/EntityBankPromptGenerator.js
//
// Endpoints used:
//   GET  /api/projects/:id/discovery        → entity surface
//   POST /api/projects/:id/generate-prompts → { entityIds }
//   POST /api/projects/:id/recommend-urls   → { entityIds }
//   POST /api/entity-sets                   → { name, prompts, urls } (saves the set)
//
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

const FALLBACK_API_BASE_URL = `${
  (typeof process !== 'undefined' && process.env && process.env.REACT_APP_API_URL) ||
  'https://geojetpack-production.up.railway.app'
}/api`;

const COLOURS = {
  teal: '#048ba8',
  tealDark: '#036d85',
  pineGreen: '#01796f',
  jetBlack: '#39393a',
  saffron: '#e9c46a',
  sandyBrown: '#f4a261',
  burntSienna: '#e76f51',
  lightGrey: '#f3f4f6',
  midGrey: '#9ca3af',
  borderGrey: '#e0e0e0',
  successGreen: '#2a9d8f',
  white: '#ffffff',
};

const FONT = '"Work Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';

const PROMPT_CLASS_LABELS = {
  A: 'Brand-direct',
  B: 'Competitive',
  C: 'Service / topic',
  D: 'Customer-segment',
  E: 'Topic-discovery',
};

const STEPS = ['Select', 'Prompts', 'URLs', 'Save'];

// ── Display helpers ───────────────────────────────────────────────────────────
function prettyClass(key) {
  if (!key) return 'Other';
  return key
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/^\w/, (c) => c.toUpperCase());
}

function strengthColour(strength) {
  switch ((strength || '').toLowerCase()) {
    case 'strong':      return COLOURS.successGreen;
    case 'moderate':    return COLOURS.saffron;
    case 'opportunity': return COLOURS.sandyBrown;
    case 'manual':      return COLOURS.midGrey;
    default:            return COLOURS.midGrey;
  }
}

// ── CSV export ────────────────────────────────────────────────────────────────
function exportPromptsCSV(prompts, projectName) {
  const kept = prompts.filter(p => p.keep && p.text.trim());
  const rows = [
    ['Class', 'Label', 'Prompt', 'Entity group'],
    ...kept.map(p => [
      p.promptClass,
      PROMPT_CLASS_LABELS[p.promptClass] || p.promptClass,
      p.text.trim(),
      (p.entityNames || []).join('; ')
    ])
  ];
  const csv = rows
    .map(r => r.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    .join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${(projectName || 'prompts').replace(/[^a-z0-9]/gi, '-').toLowerCase()}-prompts.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

// ── Response normalisers ──────────────────────────────────────────────────────
function extractProject(discoveryData) {
  const p = discoveryData?.project || discoveryData || {};
  return {
    id: p.id || null,
    name: p.name || p.project_name || 'Project',
    domain: p.domain || p.website || p.url || '',
    industry: p.industry || p.industry_label || '',
  };
}

function extractEntityGroups(discoveryData) {
  const byClass = discoveryData?.surface?.byClass || discoveryData?.byClass || {};
  const groups = {};
  Object.keys(byClass).forEach((cls) => {
    const list = (byClass[cls] || []).map((e) => ({
      id: e.id || e.entity_id,
      name: e.entity_name || e.name,
      enqClass: e.enq_class || cls,
    }));
    if (list.length) groups[cls] = list;
  });
  return groups;
}

function extractPrompts(genData) {
  const raw = genData?.prompts || [];
  return raw.map((p, i) => ({
    id: `p_${i}`,
    text: p.text || '',
    promptClass: p.prompt_class || p.promptClass || 'C',
    entityIds: p.provenance?.entity_ids || p.entity_ids || [],
    entityNames: p.provenance?.entity_names || p.entity_names || [],
    keep: true,
  }));
}

function normaliseRecommendedUrls(recData) {
  const list =
    recData?.urls ||
    recData?.recommendations ||
    recData?.recommendedUrls ||
    recData?.data?.urls ||
    [];
  return list.map((u, i) => {
    const rec = u.recommendation || u;
    const contributors = (u.contributors || rec.contributors || []).map((c) => ({
      name: c.entity_name || c.name || String(c),
      isBrand: c.is_brand === true,
    }));
    return {
      id: `u_${i}`,
      url: u.url || u.page_url || u.target_url || '',
      strength: rec.strength || u.strength || 'opportunity',
      overlap: typeof rec.overlap === 'number' ? rec.overlap : u.overlap ?? null,
      contributors,
      selected: (rec.strength || u.strength || '').toLowerCase() === 'strong',
    };
  });
}

// ── Component ─────────────────────────────────────────────────────────────────
export default function EntityBankPromptGenerator({ apiBaseUrl, userId }) {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const baseUrl = apiBaseUrl || FALLBACK_API_BASE_URL;

  const [step, setStep] = useState(0);
  const [error, setError] = useState(null);
  const [loadingProject, setLoadingProject] = useState(true);
  const [project, setProject] = useState(null);
  const [entityGroups, setEntityGroups] = useState({});
  const [activeGroup, setActiveGroup] = useState('ALL');
  const [selectedIds, setSelectedIds] = useState([]);
  const [setName, setSetName] = useState('');
  const [generating, setGenerating] = useState(false);
  const [genMeta, setGenMeta] = useState(null);
  const [prompts, setPrompts] = useState([]);
  const [recommending, setRecommending] = useState(false);
  const [urls, setUrls] = useState([]);
  const [saving, setSaving] = useState(false);

  const apiFetch = useCallback(
    async (path, options = {}) => {
      const res = await fetch(`${baseUrl}${path}`, {
        ...options,
        headers: {
          'Content-Type': 'application/json',
          'x-user-id': userId,
          ...(options.headers || {}),
        },
      });
      let data = null;
      try { data = await res.json(); } catch (_) {}
      if (!res.ok || (data && data.success === false)) {
        throw new Error((data && data.error) || `HTTP ${res.status}`);
      }
      return data;
    },
    [baseUrl, userId]
  );

  useEffect(() => {
    if (!userId || !projectId) return;
    let cancelled = false;
    (async () => {
      setLoadingProject(true);
      setError(null);
      try {
        const data = await apiFetch(`/projects/${projectId}/discovery`);
        if (cancelled) return;
        const groups = extractEntityGroups(data);
        setProject(extractProject(data));
        setEntityGroups(groups);
        if (!Object.keys(groups).length) {
          setError('No confirmed entities found. Run ENQ discovery first.');
        }
      } catch (e) {
        if (!cancelled) setError(`Couldn't load the project surface: ${e.message}`);
      } finally {
        if (!cancelled) setLoadingProject(false);
      }
    })();
    return () => { cancelled = true; };
  }, [userId, projectId, apiFetch]);

  const toggleEntity = (id) =>
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const selectedCountForGroup = (cls) =>
    (entityGroups[cls] || []).filter((e) => selectedIds.includes(e.id)).length;

  const handleGeneratePrompts = async () => {
    setError(null);
    setGenerating(true);
    try {
      const data = await apiFetch(`/projects/${projectId}/generate-prompts`, {
        method: 'POST',
        body: JSON.stringify({ entityIds: selectedIds, campaignName: setName.trim() }),
      });
      setPrompts(extractPrompts(data));
      setGenMeta({ projectedScope: data.projectedScope || null, classesSkipped: data.classesSkipped || [] });
      setStep(1);
    } catch (e) {
      setError(`Prompt generation failed: ${e.message}`);
    } finally {
      setGenerating(false);
    }
  };

  const handleRecommendUrls = async () => {
    setError(null);
    setRecommending(true);
    try {
      const data = await apiFetch(`/projects/${projectId}/recommend-urls`, {
        method: 'POST',
        body: JSON.stringify({ entityIds: selectedIds }),
      });
      setUrls(normaliseRecommendedUrls(data));
      setStep(2);
    } catch (e) {
      setError(`URL recommendation failed: ${e.message}`);
    } finally {
      setRecommending(false);
    }
  };

  const handleSave = async () => {
    setError(null);
    setSaving(true);
    try {
      const keptPrompts = prompts.filter(p => p.keep && p.text.trim());
      const selectedUrls = urls.filter(u => u.selected && u.url.trim());

      if (keptPrompts.length === 0) {
        throw new Error('Please keep at least one prompt before saving.');
      }

      // POST to create entity set with prompts and URLs
      const payload = {
        project_id: projectId,
        name: setName.trim() || `Generated ${new Date().toLocaleDateString()}`,
        description: `Generated from ${selectedIds.length} entities`,
        prompts: keptPrompts.map(p => ({
          text: p.text.trim(),
          prompt_class: p.promptClass,
          entity_ids: p.entityIds,
        })),
        urls: selectedUrls.map(u => ({
          url: u.url.trim(),
          strength: u.strength,
        })),
      };

      const saveData = await apiFetch('/entity-sets', {
        method: 'POST',
        body: JSON.stringify(payload),
      });

      setStep(3);
      // After save, user can navigate to campaign builder or elsewhere
      setTimeout(() => {
        navigate(`/project/${projectId}/campaign-builder`);
      }, 2000);
    } catch (e) {
      setError(`Failed to save prompt set: ${e.message}`);
      setSaving(false);
    }
  };

  if (!userId) {
    return <CentredMessage text="Please sign in to use the prompt generator." />;
  }

  const selectedCount = selectedIds.length;
  const keptPromptCount = prompts.filter((p) => p.keep && p.text.trim()).length;
  const selectedUrlCount = urls.filter((u) => u.selected && u.url.trim()).length;
  const groupKeys = Object.keys(entityGroups);
  const selectedEntities = groupKeys.flatMap((cls) =>
    (entityGroups[cls] || []).filter((e) => selectedIds.includes(e.id))
  );

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <div>
          <div style={styles.kicker}>Entity Bank · Prompt Generator</div>
          <h1 style={styles.h1}>{project ? project.name : 'New prompt set'}</h1>
          {project?.domain ? <div style={styles.subtle}>{project.domain}</div> : null}
        </div>
      </div>
      <StepBar current={step} />
      {error ? (
        <div style={styles.errorBox}>
          {error}
          <button style={styles.errorDismiss} onClick={() => setError(null)}>✕</button>
        </div>
      ) : null}

      {/* ── STEP 0 — SELECT ──────────────────────────────────────────────── */}
      {step === 0 && (
        <div style={styles.card}>
          {loadingProject ? (
            <CentredMessage text="Loading the project's entity surface…" inline />
          ) : (
            <>
              {project ? (
                <div style={styles.summaryRow}>
                  {project.industry ? <SummaryChip label="Industry" value={project.industry} /> : null}
                  <SummaryChip label="Entities discovered" value={String(Object.values(entityGroups).reduce((n, g) => n + g.length, 0))} />
                  <SummaryChip label="Groups" value={String(groupKeys.length)} />
                </div>
              ) : null}
              <label style={styles.label}>Prompt set name (optional)</label>
              <input
                style={styles.input}
                value={setName}
                onChange={(e) => setSetName(e.target.value)}
                placeholder="e.g. CIM AI & development prompts — Q2"
              />
              <p style={{ ...styles.helpText, marginTop: 24 }}>
                Pick a coherent set of entities — a brand, a couple of services or topics, an audience.
                Use the group filters to narrow what's shown, tick entities to add them to your basket on the right.
              </p>
              <div style={styles.buildLayout}>
                <div style={styles.buildMain}>
                  <div style={styles.groupPanel}>
                    <div style={styles.panelHeading}>Select group</div>
                    <div style={styles.lozengeRow}>
                      <Lozenge active={activeGroup === 'ALL'} onClick={() => setActiveGroup('ALL')} label="All" count={selectedCount} />
                      {groupKeys.map((cls) => (
                        <Lozenge key={cls} active={activeGroup === cls} onClick={() => setActiveGroup(cls)} label={prettyClass(cls)} total={entityGroups[cls].length} count={selectedCountForGroup(cls)} />
                      ))}
                    </div>
                  </div>
                  <div style={styles.entityPanel}>
                    <div style={styles.panelHeading}>Select entities</div>
                    <div style={styles.chipWrap}>
                      {groupKeys
                        .filter((cls) => activeGroup === 'ALL' || activeGroup === cls)
                        .map((cls) => (
                          <div key={cls} style={{ width: '100%' }}>
                            {activeGroup === 'ALL' ? <div style={styles.groupHeading}>{prettyClass(cls)}</div> : null}
                            <div style={styles.chipGroup}>
                              {entityGroups[cls].map((e) => {
                                const on = selectedIds.includes(e.id);
                                return (
                                  <button key={e.id} onClick={() => toggleEntity(e.id)} style={{ ...styles.chip, ...(on ? styles.chipOn : {}) }}>
                                    {on ? '✓ ' : ''}{e.name}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>
                </div>
                <aside style={styles.basket}>
                  <div style={styles.basketHeading}>Selected entities</div>
                  {selectedEntities.length ? (
                    <div style={styles.basketList}>
                      {selectedEntities.map((e) => (
                        <button key={e.id} style={styles.basketChip} onClick={() => toggleEntity(e.id)} title="Remove from selection">
                          <span style={styles.basketChipName}>{e.name}</span>
                          <span style={styles.basketChipClass}>{prettyClass(e.enqClass)}</span>
                          <span style={styles.basketChipRemove}>✕</span>
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div style={styles.basketEmpty}>
                      Nothing selected yet. Tick entities on the left and they'll collect here.
                    </div>
                  )}
                  <div style={styles.basketFooter}>
                    <div style={styles.subtle}>{selectedCount} {selectedCount === 1 ? 'entity' : 'entities'} selected</div>
                    <button
                      style={{ ...styles.primaryBtn, ...styles.basketBtn, ...(generating || selectedCount === 0 ? styles.btnDisabled : {}) }}
                      disabled={generating || selectedCount === 0}
                      onClick={handleGeneratePrompts}
                    >
                      {generating ? 'Generating…' : 'Generate prompts →'}
                    </button>
                  </div>
                </aside>
              </div>
            </>
          )}
        </div>
      )}

      {/* ── STEP 1 — PROMPTS ─────────────────────────────────────────────── */}
      {step === 1 && (
        <div style={styles.card}>
          <h2 style={styles.h2}>Review the prompts</h2>
          <p style={styles.helpText}>Edit the wording, or remove any that don't fit.</p>
          {genMeta?.projectedScope ? (
            <div style={styles.metaBox}>
              {genMeta.projectedScope.promptCount} prompts · {genMeta.projectedScope.targetUrlCount} target URLs
              {genMeta.projectedScope.coherenceWarning ? (
                <div style={styles.warnText}>⚠ {genMeta.projectedScope.coherenceWarning}</div>
              ) : null}
            </div>
          ) : null}
          {genMeta?.classesSkipped?.length ? (
            <div style={styles.subtle}>
              Skipped: {genMeta.classesSkipped.map((s) => `${PROMPT_CLASS_LABELS[s.class] || s.class} (${s.reason})`).join(' · ')}
            </div>
          ) : null}
          {['A', 'B', 'C', 'D', 'E'].map((cls) => {
            const rows = prompts.filter((p) => p.promptClass === cls);
            if (!rows.length) return null;
            return (
              <div key={cls} style={{ marginTop: 18 }}>
                <div style={styles.classHeading}>
                  <span style={styles.classBadge}>{cls}</span>
                  {PROMPT_CLASS_LABELS[cls] || cls}
                </div>
                {rows.map((p) => (
                  <div key={p.id} style={{ ...styles.promptRow, opacity: p.keep ? 1 : 0.4 }}>
                    <textarea
                      style={styles.promptInput}
                      value={p.text}
                      disabled={!p.keep}
                      rows={2}
                      onChange={(e) => setPrompts((prev) => prev.map((x) => (x.id === p.id ? { ...x, text: e.target.value } : x)))}
                    />
                    <div style={styles.promptMeta}>
                      {p.entityNames?.length ? <span style={styles.provenance}>from: {p.entityNames.join(', ')}</span> : null}
                      <button style={styles.linkBtn} onClick={() => setPrompts((prev) => prev.map((x) => (x.id === p.id ? { ...x, keep: !x.keep } : x)))}>
                        {p.keep ? 'Remove' : 'Restore'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            );
          })}
          <div style={styles.actionRow}>
            <button style={styles.secondaryBtn} onClick={() => setStep(0)}>← Back</button>
            <div style={styles.subtle}>{keptPromptCount} prompts kept</div>
            <button style={styles.exportBtn} onClick={() => exportPromptsCSV(prompts, project?.name)}>
              ↓ Export CSV
            </button>
            <button
              style={{ ...styles.primaryBtn, ...(recommending || keptPromptCount === 0 ? styles.btnDisabled : {}) }}
              disabled={recommending || keptPromptCount === 0}
              onClick={handleRecommendUrls}
            >
              {recommending ? 'Finding URLs…' : 'Recommend URLs →'}
            </button>
          </div>
        </div>
      )}

      {/* ── STEP 2 — URLS ────────────────────────────────────────────────── */}
      {step === 2 && (
        <div style={styles.card}>
          <h2 style={styles.h2}>Choose the URLs</h2>
          <p style={styles.helpText}>Ranked by topic overlap with your selected entities. Tick the pages to include, edit URLs, or add your own.</p>
          {urls.map((u) => (
            <div key={u.id} style={styles.urlRow}>
              <input type="checkbox" checked={u.selected} onChange={() => setUrls((prev) => prev.map((x) => (x.id === u.id ? { ...x, selected: !x.selected } : x)))} style={styles.checkbox} />
              <div style={{ flex: 1 }}>
                <input style={styles.urlInput} value={u.url} onChange={(e) => setUrls((prev) => prev.map((x) => (x.id === u.id ? { ...x, url: e.target.value } : x)))} />
                {u.contributors?.length ? (
                  <div style={styles.provenance}>{u.contributors.map((c) => (c.isBrand ? `${c.name} (brand)` : c.name)).join(', ')}</div>
                ) : null}
              </div>
              <span style={{ ...styles.strengthBadge, background: strengthColour(u.strength) }}>
                {u.strength}{typeof u.overlap === 'number' ? ` · ${u.overlap.toFixed(2)}` : ''}
              </span>
            </div>
          ))}
          <button style={styles.addBtn} onClick={() => setUrls((prev) => [...prev, { id: `u_custom_${prev.length}`, url: '', strength: 'manual', overlap: null, contributors: [], selected: true }])}>
            + Add a URL
          </button>
          <div style={styles.actionRow}>
            <button style={styles.secondaryBtn} onClick={() => setStep(1)}>← Back</button>
            <div style={styles.subtle}>{selectedUrlCount} URLs selected</div>
            <button
              style={{ ...styles.primaryBtn, ...(saving || keptPromptCount === 0 ? styles.btnDisabled : {}) }}
              disabled={saving || keptPromptCount === 0}
              onClick={handleSave}
            >
              {saving ? 'Saving…' : 'Save prompt set →'}
            </button>
          </div>
        </div>
      )}

      {/* ── STEP 3 — SAVED ───────────────────────────────────────────────── */}
      {step === 3 && (
        <div style={styles.card}>
          <div style={{ textAlign: 'center', padding: '40px 20px' }}>
            <div style={{ fontSize: 28, color: COLOURS.successGreen, marginBottom: 12 }}>✓</div>
            <h2 style={styles.h2}>Prompt set saved</h2>
            <p style={styles.helpText}>
              {keptPromptCount} prompts and {selectedUrlCount} URLs have been saved.
            </p>
            <p style={{ ...styles.subtle, marginTop: 16 }}>
              Redirecting to campaign builder so you can import this prompt set…
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Sub-components ────────────────────────────────────────────────────────────
function StepBar({ current }) {
  return (
    <div style={styles.stepBar}>
      {STEPS.map((label, i) => (
        <div key={label} style={styles.stepItem}>
          <div style={{ ...styles.stepDot, background: i <= current ? COLOURS.teal : COLOURS.borderGrey, color: i <= current ? COLOURS.white : COLOURS.midGrey }}>
            {i + 1}
          </div>
          <span style={{ color: i === current ? COLOURS.jetBlack : COLOURS.midGrey }}>{label}</span>
          {i < STEPS.length - 1 ? <div style={styles.stepLine} /> : null}
        </div>
      ))}
    </div>
  );
}

function Lozenge({ active, onClick, label, count, total }) {
  return (
    <button onClick={onClick} style={{ ...styles.lozenge, ...(active ? styles.lozengeActive : {}) }}>
      {label}
      {typeof total === 'number' ? (
        <span style={styles.lozengeCount}>{count > 0 ? `${count}/${total}` : total}</span>
      ) : count > 0 ? (
        <span style={styles.lozengeCount}>{count}</span>
      ) : null}
    </button>
  );
}

function SummaryChip({ label, value }) {
  return (
    <div style={styles.summaryChip}>
      <div style={styles.summaryChipValue}>{value}</div>
      <div style={styles.summaryChipLabel}>{label}</div>
    </div>
  );
}

function CentredMessage({ text, inline }) {
  return (
    <div style={{ ...styles.centred, ...(inline ? { minHeight: 120 } : { minHeight: '60vh' }) }}>
      <div style={styles.subtle}>{text}</div>
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────
const styles = {
  page: { fontFamily: FONT, maxWidth: 880, margin: '0 auto', padding: '32px 20px 80px', color: COLOURS.jetBlack },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' },
  kicker: { fontSize: 12, letterSpacing: 1, textTransform: 'uppercase', color: COLOURS.teal },
  h1: { fontSize: 28, fontWeight: 600, margin: '4px 0 0' },
  h2: { fontSize: 20, fontWeight: 600, margin: '0 0 4px' },
  subtle: { fontSize: 13, color: COLOURS.midGrey },
  card: { background: COLOURS.white, border: `1px solid ${COLOURS.borderGrey}`, borderRadius: 12, padding: 28, marginTop: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.04)' },
  stepBar: { display: 'flex', alignItems: 'center', gap: 8, marginTop: 24 },
  stepItem: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 500 },
  stepDot: { width: 26, height: 26, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 600 },
  stepLine: { width: 28, height: 2, background: COLOURS.borderGrey, marginLeft: 4 },
  summaryRow: { display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' },
  summaryChip: { background: COLOURS.lightGrey, borderRadius: 10, padding: '10px 16px', minWidth: 90 },
  summaryChipValue: { fontSize: 20, fontWeight: 600, color: COLOURS.tealDark },
  summaryChipLabel: { fontSize: 11, color: COLOURS.midGrey, textTransform: 'uppercase', letterSpacing: 0.5 },
  label: { display: 'block', fontSize: 14, fontWeight: 600, marginBottom: 6 },
  helpText: { fontSize: 14, color: '#555', margin: '0 0 14px', lineHeight: 1.5 },
  input: { width: '100%', padding: '12px 14px', fontSize: 15, fontFamily: FONT, border: `1px solid ${COLOURS.borderGrey}`, borderRadius: 8, boxSizing: 'border-box' },
  groupPanel: { background: COLOURS.lightGrey, border: `1px solid ${COLOURS.borderGrey}`, borderRadius: 10, padding: '16px 18px', marginBottom: 16 },
  entityPanel: { background: COLOURS.white, border: `1px solid ${COLOURS.borderGrey}`, borderRadius: 10, padding: '16px 18px' },
  panelHeading: { fontSize: 12, fontWeight: 700, color: COLOURS.midGrey, textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 12 },
  buildLayout: { display: 'flex', gap: 20, alignItems: 'flex-start', flexWrap: 'wrap', marginTop: 4 },
  buildMain: { flex: '1 1 420px', minWidth: 0 },
  basket: { flex: '1 1 240px', minWidth: 240, maxWidth: 320, position: 'sticky', top: 20, background: '#fdf8ec', border: '1px solid #ecd9a0', borderRadius: 12, padding: 18, boxShadow: '0 1px 3px rgba(0,0,0,0.04)' },
  basketHeading: { fontSize: 12, fontWeight: 700, color: COLOURS.tealDark, textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 12 },
  basketList: { display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 4 },
  basketChip: { display: 'flex', alignItems: 'center', gap: 8, width: '100%', textAlign: 'left', background: COLOURS.pineGreen, color: COLOURS.white, border: 'none', borderRadius: 8, padding: '8px 12px', fontSize: 13, fontFamily: FONT, cursor: 'pointer' },
  basketChipName: { flex: 1, lineHeight: 1.3 },
  basketChipClass: { fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.4, opacity: 0.75, whiteSpace: 'nowrap' },
  basketChipRemove: { fontSize: 12, opacity: 0.85 },
  basketEmpty: { fontSize: 13, color: COLOURS.midGrey, lineHeight: 1.5, margin: '4px 0 8px' },
  basketFooter: { borderTop: '1px solid rgba(0,0,0,0.08)', paddingTop: 14, marginTop: 14 },
  basketBtn: { width: '100%', marginTop: 10, textAlign: 'center' },
  lozengeRow: { display: 'flex', flexWrap: 'wrap', gap: 8 },
  lozenge: { border: `1px solid ${COLOURS.borderGrey}`, background: COLOURS.white, borderRadius: 999, padding: '7px 14px', fontSize: 13, fontFamily: FONT, cursor: 'pointer', color: COLOURS.jetBlack, display: 'inline-flex', alignItems: 'center', gap: 6 },
  lozengeActive: { background: COLOURS.teal, borderColor: COLOURS.teal, color: COLOURS.white },
  lozengeCount: { background: 'rgba(0,0,0,0.12)', borderRadius: 999, padding: '0 7px', fontSize: 11, fontWeight: 600 },
  chipWrap: { display: 'flex', flexWrap: 'wrap', gap: 16 },
  groupHeading: { fontSize: 12, fontWeight: 600, color: COLOURS.midGrey, textTransform: 'uppercase', letterSpacing: 0.5, margin: '6px 0' },
  chipGroup: { display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 8 },
  chip: { border: `1px solid ${COLOURS.borderGrey}`, background: COLOURS.white, borderRadius: 8, padding: '8px 12px', fontSize: 13, fontFamily: FONT, cursor: 'pointer', color: COLOURS.jetBlack },
  chipOn: { background: COLOURS.pineGreen, borderColor: COLOURS.pineGreen, color: COLOURS.white },
  actionRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginTop: 28, paddingTop: 20, borderTop: `1px solid ${COLOURS.lightGrey}` },
  primaryBtn: { background: COLOURS.teal, color: COLOURS.white, border: 'none', borderRadius: 8, padding: '12px 22px', fontSize: 15, fontWeight: 600, fontFamily: FONT, cursor: 'pointer' },
  secondaryBtn: { background: COLOURS.white, color: COLOURS.jetBlack, border: `1px solid ${COLOURS.borderGrey}`, borderRadius: 8, padding: '12px 18px', fontSize: 14, fontFamily: FONT, cursor: 'pointer' },
  exportBtn: { background: COLOURS.white, color: COLOURS.pineGreen, border: `1px solid ${COLOURS.pineGreen}`, borderRadius: 8, padding: '12px 18px', fontSize: 14, fontFamily: FONT, cursor: 'pointer' },
  btnDisabled: { opacity: 0.45, cursor: 'not-allowed' },
  addBtn: { background: 'transparent', color: COLOURS.teal, border: `1px dashed ${COLOURS.teal}`, borderRadius: 8, padding: '10px 16px', fontSize: 14, fontFamily: FONT, cursor: 'pointer', marginTop: 12 },
  linkBtn: { background: 'none', border: 'none', color: COLOURS.burntSienna, fontSize: 13, fontFamily: FONT, cursor: 'pointer', padding: 0 },
  metaBox: { background: COLOURS.lightGrey, borderRadius: 8, padding: '10px 14px', fontSize: 13, marginBottom: 8 },
  warnText: { color: COLOURS.burntSienna, marginTop: 4 },
  classHeading: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, fontWeight: 600, margin: '6px 0 10px' },
  classBadge: { background: COLOURS.saffron, color: COLOURS.jetBlack, borderRadius: 6, padding: '2px 8px', fontSize: 12, fontWeight: 700 },
  promptRow: { marginBottom: 12 },
  promptInput: { width: '100%', padding: '10px 12px', fontSize: 14, fontFamily: FONT, border: `1px solid ${COLOURS.borderGrey}`, borderRadius: 8, boxSizing: 'border-box', resize: 'vertical' },
  promptMeta: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 4 },
  provenance: { fontSize: 12, color: COLOURS.midGrey, fontStyle: 'italic' },
  urlRow: { display: 'flex', alignItems: 'flex-start', gap: 12, padding: '12px 0', borderBottom: `1px solid ${COLOURS.lightGrey}` },
  checkbox: { marginTop: 12, width: 16, height: 16 },
  urlInput: { width: '100%', padding: '8px 10px', fontSize: 13, fontFamily: FONT, border: `1px solid ${COLOURS.borderGrey}`, borderRadius: 6, boxSizing: 'border-box' },
  strengthBadge: { color: COLOURS.white, borderRadius: 6, padding: '4px 10px', fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap', marginTop: 6 },
  centred: { display: 'flex', alignItems: 'center', justifyContent: 'center' },
  errorBox: { background: '#fdecea', border: `1px solid ${COLOURS.burntSienna}`, color: '#a23a26', borderRadius: 8, padding: '12px 16px', marginTop: 16, fontSize: 14, display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  errorDismiss: { background: 'none', border: 'none', color: '#a23a26', cursor: 'pointer', fontSize: 16 },
};
